import React, { useState, useRef, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";

const GlobalStyles = createGlobalStyle`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 780px;
  max-width: 1000px;
  display: flex;
  justify-content: center;
  margin-inline: auto;
  background-color: black;

  ${({ $isTheaterMode, $isFullScreen }) =>
    ($isTheaterMode || $isFullScreen) &&
    `
    max-width: initial;
    width: 100%;
  `}

  ${({ $isTheaterMode }) =>
    $isTheaterMode &&
    `
    max-height: 90vh;
  `}

  ${({ $isFullScreen }) =>
    $isFullScreen &&
    `
    max-height: 100vh;
  `}

  ${({ $isPaused }) =>
    $isPaused &&
    `
    .pause-icon {
      display: none;
    }
  `}

  ${({ $isPaused }) =>
    !$isPaused &&
    `
    .play-icon {
      display: none;
    }
  `}

  ${({ $volumeLevel }) => `
    .volume-high-icon { display: ${
      $volumeLevel === "high" ? "block" : "none"
    }; }
    .volume-low-icon { display: ${$volumeLevel === "low" ? "block" : "none"}; }
    .volume-muted-icon { display: ${
      $volumeLevel === "muted" ? "block" : "none"
    }; }
  `}

  ${({ $isScrubbing }) =>
    $isScrubbing &&
    `
    .thumbnail-img {
      display: block;
    }
    .preview-img,
    .timeline::before {
      display: block;
    }
    .thumb-indicator {
      --scale: 1;
    }
    .timeline {
      height: 100%;
    }
  `}
`;

const VideoElement = styled.video`
  width: 100%;
`;

const VideoControlsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  color: white;
  z-index: 100;
  opacity: 0;
  transition: opacity 150ms ease-in-out;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75), transparent);
    width: 100%;
    aspect-ratio: 6 / 1;
    z-index: -1;
    pointer-events: none;
  }

  ${VideoContainer}:hover &,
  ${VideoContainer}:focus-within &,
  ${VideoContainer}.paused & {
    opacity: 1;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem;
  align-items: center;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  padding: 0;
  height: 30px;
  width: 30px;
  font-size: 1.1rem;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 150ms ease-in-out;

  &:hover {
    opacity: 1;
  }

  ${({ $wide }) =>
    $wide &&
    `
    width: 50px;
  `}
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
`;

const VolumeSlider = styled.input`
  width: 0;
  transform-origin: left;
  transform: scaleX(0);
  transition: width 150ms ease-in-out, transform 150ms ease-in-out;

  ${VolumeContainer}:hover &,
  &:focus-within {
    width: 100px;
    transform: scaleX(1);
  }
`;

const DurationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-grow: 1;
`;

const TimelineContainer = styled.div`
  height: 7px;
  margin-inline: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Timeline = styled.div`
  background-color: rgba(100, 100, 100, 0.5);
  height: 3px;
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: calc(100% - var(--preview-position) * 100%);
    background-color: rgb(150, 150, 150);
    display: none;
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: calc(100% - var(--progress-position) * 100%);
    background-color: red;
  }
`;

const ThumbIndicator = styled.div`
  --scale: 0;
  position: absolute;
  transform: translateX(-50%) scale(var(--scale));
  height: 200%;
  top: -50%;
  left: calc(var(--progress-position) * 100%);
  background-color: red;
  border-radius: 50%;
  transition: transform 150ms ease-in-out;
  aspect-ratio: 1 / 1;
`;
const Player = ({ videoUrl }) => {
  const [isPaused, setIsPaused] = useState(true);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState("high");
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");
  const [volume, setVolume] = useState(1);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [progress, setProgress] = useState(0);

  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const wasPaused = useRef(false);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("volumechange", handleVolumeChange);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);

      return () => {
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("volumechange", handleVolumeChange);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
      };
    }
  }, []);

  const handleLoadedData = () => {
    setDuration(formatDuration(videoRef.current.duration));
  };

  const handleTimeUpdate = () => {
    setCurrentTime(formatDuration(videoRef.current.currentTime));
    setProgress(videoRef.current.currentTime / videoRef.current.duration);
  };

  const handlePlay = () => {
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const togglePlay = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  };

  const toggleTheaterMode = () => {
    setIsTheaterMode((prev) => !prev);
  };

  const toggleFullScreenMode = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullScreen(!isFullScreen);
  };

  const toggleMiniPlayerMode = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await videoRef.current?.requestPictureInPicture();
      }
      setIsMiniPlayer(!isMiniPlayer);
    } catch (error) {
      console.error("PiP failed:", error);
    }
  };

  const handleVolumeChange = () => {
    setVolume(videoRef.current.volume);
    let newVolumeLevel;
    if (videoRef.current.muted || videoRef.current.volume === 0) {
      newVolumeLevel = "muted";
    } else if (videoRef.current.volume >= 0.5) {
      newVolumeLevel = "high";
    } else {
      newVolumeLevel = "low";
    }
    setVolumeLevel(newVolumeLevel);
  };

  const handleVolumeSlider = (e) => {
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
    videoRef.current.muted = newVolume === 0;
  };

  const toggleMute = () => {
    videoRef.current.muted = !videoRef.current.muted;
  };

  const changePlaybackSpeed = () => {
    const newPlaybackRate = videoRef.current.playbackRate + 0.25;
    if (newPlaybackRate > 2) {
      videoRef.current.playbackRate = 0.25;
    } else {
      videoRef.current.playbackRate = newPlaybackRate;
    }
    setPlaybackRate(videoRef.current.playbackRate);
  };

  const handleTimelineUpdate = (e) => {
    const rect = timelineContainerRef.current.getBoundingClientRect();
    const percent =
      Math.min(Math.max(0, e.clientX - rect.x), rect.width) / rect.width;
    timelineContainerRef.current.style.setProperty(
      "--preview-position",
      percent
    );

    if (isScrubbing) {
      e.preventDefault();
      timelineContainerRef.current.style.setProperty(
        "--progress-position",
        percent
      );
    }
  };

  const toggleScrubbing = (e) => {
    const rect = timelineContainerRef.current.getBoundingClientRect();
    const percent =
      Math.min(Math.max(0, e.clientX - rect.x), rect.width) / rect.width;
    setIsScrubbing((e.buttons & 1) === 1);

    if ((e.buttons & 1) === 1) {
      wasPaused.current = videoRef.current.paused;
      videoRef.current.pause();
    } else {
      videoRef.current.currentTime = percent * videoRef.current.duration;
      if (!wasPaused.current) {
        videoRef.current.play();
      }
    }
    handleTimelineUpdate(e);
  };

  const formatDuration = (time) => {
    const seconds = Math.floor(time % 60);
    const minutes = Math.floor(time / 60) % 60;
    const hours = Math.floor(time / 3600);

    if (hours === 0) {
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tagName = document.activeElement.tagName.toLowerCase();
      if (tagName === "input") return;

      switch (e.key.toLowerCase()) {
        case " ":
          if (tagName === "button") return;
        case "k":
          togglePlay();
          break;
        case "f":
          toggleFullScreenMode();
          break;
        case "t":
          toggleTheaterMode();
          break;
        case "i":
          toggleMiniPlayerMode();
          break;
        case "m":
          toggleMute();
          break;
        case "arrowleft":
        case "j":
          videoRef.current.currentTime -= 5;
          break;
        case "arrowright":
        case "l":
          videoRef.current.currentTime += 5;
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <GlobalStyles />
      <VideoContainer
        ref={videoContainerRef}
        $isPaused={isPaused}
        $isTheaterMode={isTheaterMode}
        $isFullScreen={isFullScreen}
        $isMiniPlayer={isMiniPlayer}
        $isScrubbing={isScrubbing}
        $volumeLevel={volumeLevel}
      >
        <VideoElement
          ref={videoRef}
          //   src="https://openmic-videos.s3.ap-south-1.amazonaws.com/Screen+Recording+2024-12-11+at+12.39.53+PM.mov"
          src={videoUrl}
          onClick={togglePlay}
        />

        <VideoControlsContainer>
          <TimelineContainer
            ref={timelineContainerRef}
            onMouseMove={handleTimelineUpdate}
            onMouseDown={toggleScrubbing}
            style={{
              "--progress-position": progress,
              "--preview-position": progress,
            }}
          >
            <Timeline>
              <ThumbIndicator />
            </Timeline>
          </TimelineContainer>

          <Controls>
            <ControlButton onClick={togglePlay}>
              <svg className="play-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z" />
              </svg>
              <svg className="pause-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M14,19H18V5H14M6,19H10V5H6V19Z" />
              </svg>
            </ControlButton>

            <VolumeContainer>
              <ControlButton onClick={toggleMute}>
                <svg className="volume-high-icon" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z"
                  />
                </svg>
                <svg className="volume-low-icon" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M5,9V15H9L14,20V4L9,9M18.5,12C18.5,10.23 17.5,8.71 16,7.97V16C17.5,15.29 18.5,13.76 18.5,12Z"
                  />
                </svg>
                <svg className="volume-muted-icon" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,4L9.91,6.09L12,8.18M4.27,3L3,4.27L7.73,9H3V15H7L12,20V13.27L16.25,17.53C15.58,18.04 14.83,18.46 14,18.7V20.77C15.38,20.45 16.63,19.82 17.68,18.96L19.73,21L21,19.73L12,10.73M19,12C19,12.94 18.8,13.82 18.46,14.64L19.97,16.15C20.62,14.91 21,13.5 21,12C21,7.72 18,4.14 14,3.23V5.29C16.89,6.15 19,8.83 19,12M16.5,12C16.5,10.23 15.5,8.71 14,7.97V10.18L16.45,12.63C16.5,12.43 16.5,12.21 16.5,12Z"
                  />
                </svg>
              </ControlButton>
              <VolumeSlider
                type="range"
                min="0"
                max="1"
                step="any"
                value={volume}
                onChange={handleVolumeSlider}
              />
            </VolumeContainer>

            <DurationContainer>
              <div>{currentTime}</div>/<div>{duration}</div>
            </DurationContainer>

            <ControlButton className="captions-btn">
              <svg viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M18,11H16.5V10.5H14.5V13.5H16.5V13H18V14A1,1 0 0,1 17,15H14A1,1 0 0,1 13,14V10A1,1 0 0,1 14,9H17A1,1 0 0,1 18,10M11,11H9.5V10.5H7.5V13.5H9.5V13H11V14A1,1 0 0,1 10,15H7A1,1 0 0,1 6,14V10A1,1 0 0,1 7,9H10A1,1 0 0,1 11,10M19,4H5C3.89,4 3,4.89 3,6V18A2,2 0 0,0 5,20H19A2,2 0 0,0 21,18V6C21,4.89 20.1,4 19,4Z"
                />
              </svg>
            </ControlButton>

            <ControlButton $wide onClick={changePlaybackSpeed}>
              {playbackRate}x
            </ControlButton>

            <ControlButton onClick={toggleTheaterMode}>
              <svg className="tall" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M19 6H5c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H5V8h14v8z"
                />
              </svg>
              {/* <svg className="wide" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M19 7H5c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm0 8H5V9h14v6z"
                />
              </svg> */}
            </ControlButton>

            <ControlButton onClick={toggleMiniPlayerMode}>
              <svg viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7h9v6h-9z"
                />
              </svg>
            </ControlButton>

            <ControlButton onClick={toggleFullScreenMode}>
              <svg className="open" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                />
              </svg>
              <svg className="close" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                />
              </svg>
            </ControlButton>
          </Controls>
        </VideoControlsContainer>
      </VideoContainer>
    </>
  );
};

export default Player;
